/**
 * The combining marks `normalize("NFD")` splits off an accented letter.
 *
 * Built from escapes rather than written as a literal character class: those
 * characters are invisible in an editor, survive a copy-paste badly, and are
 * exactly the kind of thing a formatter "tidies" into a different range.
 */
const COMBINING_MARKS = new RegExp("[\u0300-\u036f]", "g");

/**
 * Turn a practice's name into a subdomain it will recognise.
 *
 * **Suggested, never imposed.** The field stays editable and stops following
 * the name the moment somebody touches it — a practice that wants `cedar` for
 * "Cedar Dental Care Beirut" is right, and a form that keeps overwriting them
 * is the kind of helpfulness people learn to fight.
 *
 * Accents are folded rather than dropped, so "Clinique Sainte-Thérèse" gives
 * `clinique-sainte-therese` and not `clinique-sainte-thrse`. Anything left that
 * a DNS label cannot hold becomes a hyphen, runs collapse, and the ends are
 * trimmed — the API's own pattern refuses a leading or trailing one.
 */
export function slugify(name: string): string {
  return settleSlug(slugifyTyped(name));
}

/**
 * The same folding, but for a field somebody is still typing into.
 *
 * **A trailing hyphen survives here, and that is the entire point.** `slugify`
 * trims one, which is right for a finished name and wrong for a keystroke: at
 * the instant you type the hyphen in "my-clinic" it *is* trailing, so trimming
 * on every change swallows it and the field can never hold a hyphenated name at
 * all. Typing "my-clinic" produced `myclinic` — found by the sign-up soak on
 * 2026-09-07, on the product's only public screen, affecting every practice
 * whose name is more than one word.
 *
 * A *leading* hyphen is still dropped, because unlike a trailing one it is never
 * on the way to something valid.
 */
export function slugifyTyped(value: string): string {
  return value
    .normalize("NFD")
    .replace(COMBINING_MARKS, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+/, "")
    .slice(0, 63);
}

/**
 * What that field *means*, as opposed to what it shows — the value to check for
 * availability, preview as an address, and send.
 *
 * Separate from `slugifyTyped` so the hyphen a person is typing through never
 * reaches the availability query or the API's pattern, both of which refuse it.
 */
export function settleSlug(value: string): string {
  return value.replace(/-+$/g, "");
}

/**
 * The host this browser would give a clinic with that slug.
 *
 * Mirrors the API's `clinicOrigin` — a leading `app.` label is replaced rather
 * than prefixed, so `app.dentacore.io` gives `cedar.dentacore.io` and not a
 * fourth level nobody has a certificate for. It is derived rather than
 * configured for the same reason the server derives it: two settings that must
 * agree, one of which is only exercised at sign-up, is a configuration bug
 * discovered in front of a customer.
 */
export function addressFor(slug: string, host: string = window.location.host): string {
  const [hostname = "", port] = host.split(":");
  const base = hostname.startsWith("app.") ? hostname.slice("app.".length) : hostname;
  const shown = slug.length > 0 ? slug : "your-practice";
  return port === undefined ? `${shown}.${base}` : `${shown}.${base}:${port}`;
}
